import type { ErrorEnvelope, GatewayError } from '@/types/api'
import { peelEnvelope } from '@/lib/transport'
import { reportError } from '@/lib/report'

// Backend serves every module route under the /api namespace
// (createServer({ basePath: '/api' })) — the join happens here, once, so
// call-site literals stay unprefixed (apiFetch('/models') → /api/models).
// VITE_API_BASE is the API origin/base only ('' = same-origin via proxy).
const API_BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '') + '/api'

// /health and /ready are core probes mounted at ROOT — the only endpoints
// outside the /api namespace (vite/nginx proxy them bare). Callers pass them
// unprefixed and they stay unprefixed on the wire.
const ROOT_PROBE_PATHS = new Set(['/health', '/ready'])

export class ApiError extends Error {
  code: string
  status: number
  details?: unknown
  retryAfter?: number | null
  /** Correlation ids echoed off the failing response — `x-request-id` on
   *  gateway calls, `x-trace-id` everywhere. A bug report quoting them
   *  joins the backend fact row directly. */
  requestId?: string
  traceId?: string

  constructor(envelope: ErrorEnvelope & { retryAfter?: number | null }, corr?: { requestId?: string | null; traceId?: string | null }) {
    super(envelope.message)
    this.name = 'ApiError'
    this.code = envelope.code
    this.status = envelope.status
    this.details = envelope.details
    this.retryAfter = envelope.retryAfter ?? null
    if (corr?.requestId) this.requestId = corr.requestId
    if (corr?.traceId) this.traceId = corr.traceId
  }
}

export class GatewayApiError extends Error {
  code: string
  type: string
  status: number
  retryAfter?: number | null
  /** Same correlation contract as ApiError — the gateway stamps
   *  x-request-id on every response and mirrors it into error.request_id. */
  requestId?: string
  traceId?: string

  constructor(g: GatewayError['error'], status: number, retryAfter?: number | null, corr?: { requestId?: string | null; traceId?: string | null }) {
    super(g.message)
    this.name = 'GatewayApiError'
    this.code = g.code
    this.type = g.type
    this.status = status
    this.retryAfter = retryAfter ?? g.retry_after ?? null
    if (corr?.requestId) this.requestId = corr.requestId
    if (corr?.traceId) this.traceId = corr.traceId
  }
}

/** THE shared translator shape — one declaration for pages, hooks and the error taxonomy. */
export type TranslateFn = (key: string, opts?: Record<string, unknown>) => string

/**
 * Canonical error rendering shared by query/mutation call sites:
 * ApiError → `${code}: ${message}`; any other Error (GatewayApiError included)
 * → message; anything else → the per-site fallback (each site passes its own
 * literal — there is no shared default wording). Backend messages may already
 * embed the code prefix — never show "X: X: …".
 */
export function formatApiError(e: unknown, fallback?: string): string {
  if (e instanceof ApiError) return e.message.startsWith(`${e.code}: `) ? e.message : `${e.code}: ${e.message}`
  if (e instanceof Error) return e.message
  return fallback ?? ''
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  // REACT-NET-001 (security spec): every caller passes a relative resource
  // path — an absolute or protocol-relative one would send
  // credentials:'include' to an arbitrary origin, so refuse loudly before any
  // fetch instead of forwarding it.
  if (/^(https?:)?\/\//i.test(path)) {
    throw new Error(`buildUrl: relative resource paths only — refusing absolute URL target "${path}"`)
  }
  // The /api namespace prefix is THIS join's job (API_BASE above): a caller
  // passing `/api/...` would double-prefix to /api/api/... on the wire.
  // `/api-keys` is the legitimate near-miss — hence the boundary-exact
  // `(\/|$)`, never `\b` (the `-` in /api-keys is a word boundary too).
  if (/^\/api(\/|$)/i.test(path)) {
    throw new Error(`buildUrl: path already carries the /api namespace — pass the unprefixed resource path instead of "${path}"`)
  }
  const url = `${ROOT_PROBE_PATHS.has(path) ? '' : API_BASE}${path}`
  if (!params) return url
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && `${v}` !== '') qs.set(k, String(v))
  }
  const q = qs.toString()
  return q ? `${url}${url.includes('?') ? '&' : '?'}${q}` : url
}

/**
 * THE single Retry-After parse path — header seconds to a number, null when
 * absent or non-numeric. Consumed by the error parser, gatewayFetch, and the
 * caller-layer request policy (src/lib/request-policy.ts); nothing else reads
 * the header.
 */
export function parseRetryAfter(res: Response): number | null {
  const v = res.headers.get('Retry-After')
  if (!v) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Auth paths whose 401 is a flow answer (bad code, expired session probe)
 * rather than a stale cookie — they never trigger the reactive
 * /auth/refresh replay. Phone-OTP paths stay exempt because a replayed
 * login would burn a second verify attempt on the same OTP.
 */
const REACTIVE_REFRESH_EXEMPT = new Set([
  '/auth/refresh',
  '/auth/logout',
  '/auth/me',
  '/auth/phone/send-code',
  '/auth/phone/verify',
  '/auth/phone/register',
  '/auth/phone/login',
  '/auth/phone/bind',
])

let refreshInFlight: Promise<boolean> | null = null
/**
 * Single-flight POST /auth/refresh — rotates the session cookie; resolves
 * true when the backend accepted the refresh. Concurrent 401s share one
 * refresh call instead of stampeding the endpoint.
 */
function refreshSessionCookie(): Promise<boolean> {
  refreshInFlight ??= fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

async function parseError(res: Response): Promise<never> {
  const err: unknown = await parseErrorInner(res).catch((e: unknown) => e)
  // Server-side failures get one client-side report — the user's view of
  // a 5xx (which page, which request id) is the half the CP access log
  // can't see. 4xx/401/429/503-cold-start are handled flow, not defects.
  if (res.status >= 500 && (err instanceof ApiError || err instanceof GatewayApiError)) {
    reportError(err, 'api_error')
  }
  throw err
}

/**
 * THE single wire-error classifier — parseErrorInner and gatewayFetch both
 * build their typed errors here, so every dialect maps to exactly one class on
 * either path (the caller-layer policy inherits the same table):
 *   {error:{type,code,…}}  gateway       → GatewayApiError
 *   {error:{code,…}}       SaasKit, no type → ApiError
 *   {code,message,…}       SaasKit flat  → ApiError
 *   unrecognized / malformed JSON, raw text → ApiError `http.${status}`
 * `text` is the response body read exactly once by the caller — JSON is probed
 * from that text in place, so an unrecognized or malformed body keeps its raw
 * message on the fallback instead of degrading to the status text.
 */
function classifyErrorBody(res: Response, text: string, retryAfter: number | null): ApiError | GatewayApiError {
  // Correlation ids ride every response — x-request-id is gateway-issued,
  // x-trace-id is core-mounted; the error envelope's error.request_id is the
  // same gateway id and wins over the header (envelopes minted before the
  // handler ran carry only the body copy).
  const traceId = res.headers.get('x-trace-id')
  let requestId = res.headers.get('x-request-id')
  const ct = res.headers.get('content-type') ?? ''
  if (ct.includes('application/json')) {
    let body: unknown = null
    try {
      body = JSON.parse(text)
    } catch {
      // malformed JSON — the raw text is the message (text fallback below)
    }
    if (body && typeof body === 'object' && 'error' in body) {
      const inner: unknown = body.error
      if (inner && typeof inner === 'object') {
        // wire fields of the two nested dialects — probed by type below
        const e = inner as { type?: unknown; code?: unknown; message?: unknown; details?: unknown; request_id?: unknown }
        if (typeof e.request_id === 'string') requestId = e.request_id
        // `error.type` exists only in the OpenAI-shaped gateway envelope; the
        // SaasKit control-plane envelope {success:false,error:{code,message,details?}}
        // carries no type and maps to ApiError.
        if (typeof e.type === 'string' && typeof e.code === 'string') {
          return new GatewayApiError(inner as GatewayError['error'], res.status, retryAfter, { requestId, traceId })
        }
        if (typeof e.code === 'string') {
          return new ApiError({
            code: e.code,
            message: typeof e.message === 'string' ? e.message : res.statusText,
            status: res.status,
            details: e.details,
            retryAfter,
          }, { requestId, traceId })
        }
      }
    }
    if (body && typeof body === 'object' && 'code' in body) {
      // flat SaasKit dialect — spread into the ErrorEnvelope constructor arg
      const e = body as ErrorEnvelope
      return new ApiError({ ...e, status: e.status ?? res.status, retryAfter }, { requestId, traceId })
    }
  }
  return new ApiError({ code: `http.${res.status}`, message: text || res.statusText, status: res.status, retryAfter }, { requestId, traceId })
}

/**
 * Shared error-shape normalizer for a non-OK response: the gateway dialect
 * ({error:{type,code,…}}), the SaasKit dialects ({error:{code,…}} and flat
 * {code,message,…}), unrecognized JSON, and raw text bodies all become
 * GatewayApiError / ApiError here. Exported for the caller-layer request
 * policy (src/lib/request-policy.ts) so pages never probe envelopes
 * themselves; parseError wraps this with the 5xx client report for apiFetch.
 */
export async function parseErrorInner(res: Response): Promise<never> {
  // ONE body read — the classifier probes JSON from this text, so an
  // unrecognized or malformed JSON body keeps its raw message on the fallback.
  const text = await res.text().catch(() => res.statusText)
  throw classifyErrorBody(res, text, parseRetryAfter(res))
}

/**
 * Control-plane fetch — HttpOnly cookie auth.
 * Always `credentials:"include"`, same-origin via Vite proxy.
 * Every org-scoped call must pass `orgId` via query (resolveOrg steals :id).
 * JSON responses get ONE deterministic transform: transport's `peelEnvelope`
 * strips the standard {success,data}/{data} envelope. The declared T is the
 * post-envelope wire shape — each resource fetcher digs its own keys
 * ({jobs: T[]} → .jobs), so a new backend sibling can never change a
 * fetcher's return type or be silently dropped.
 */
export async function apiFetch<T>(path: string, init?: RequestInit & { params?: Record<string, string | number | undefined>; orgId?: string }): Promise<T> {
  const { params, orgId, ...rest } = init ?? {}
  const mergedParams: Record<string, string | number | undefined> = { ...params }
  if (orgId) mergedParams.orgId = orgId
  const url = buildUrl(path, Object.keys(mergedParams).length ? mergedParams : undefined)

  const headers = new Headers(rest.headers)
  if (rest.body && !headers.has('Content-Type') && !(rest.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  let res: Response
  try {
    res = await fetch(url, { ...rest, headers, credentials: 'include' })
  } catch (err) {
    // Network-level failure (CP unreachable / CORS / aborted DNS) — no
    // response, no status; this is the report the backend never sees.
    reportError(err, 'api_error', { extra: { endpoint: path } })
    throw err
  }
  // Reactive session renewal (frontend-contract §2.1): a 401 on a guarded
  // resource gets ONE /auth/refresh attempt, then the original request is
  // replayed with the rotated cookie. If refresh fails the original 401 is
  // thrown — Protected's redirect to /login stays the single owner of the
  // unauthenticated → /login transition. FormData bodies cannot replay
  // (stream already consumed), so they surface the 401 directly.
  if (res.status === 401 && !REACTIVE_REFRESH_EXEMPT.has(path)) {
    const refreshed = await refreshSessionCookie()
    if (refreshed && (rest.body === undefined || typeof rest.body === 'string')) {
      try {
        res = await fetch(url, { ...rest, headers, credentials: 'include' })
      } catch (err) {
        reportError(err, 'api_error', { extra: { endpoint: path, replay: true } })
        throw err
      }
    }
  }
  if (!res.ok) await parseError(res)
  if (res.status === 204) return undefined as T
  const ct2 = res.headers.get('content-type') ?? ''
  if (ct2.includes('application/json')) {
    const json = (await res.json()) as unknown
    return peelEnvelope<T>(json)
  }
  return (await res.text()) as unknown as T
}

/**
 * Gateway fetch — Bearer sk-* auth (cookie is ignored on /v1/*).
 * `model` field = deployment `name` (not id), see frontend-contract.md §7.3.
 *
 * Non-OK contract: every non-OK response is consumed here and thrown as a
 * typed ApiError / GatewayApiError from `classifyErrorBody` (same dialect
 * table as parseErrorInner — unrecognized bodies keep their raw text as
 * `message`), with the 5xx client report. Callers only ever receive `ok`
 * responses (2xx JSON or `text/event-stream` pass through untouched). Still
 * retry-free: 503 cold_start + 429 arrive as typed errors like any other
 * non-OK and are the caller-layer policy's (src/lib/request-policy.ts)
 * business per frontend-contract §4.3.
 */
export async function gatewayFetch(path: string, apiKey: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  const url = buildUrl(path)
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
      signal,
      credentials: 'omit', // dual-auth isolation: cookie ignored on /v1/* per contract §2.2
    })
  } catch (err) {
    reportError(err, 'api_error', { extra: { endpoint: path } })
    throw err
  }
  // Non-OK is an error either way: classify through THE shared error parser —
  // the same dialect table as parseErrorInner — so a SaasKit {error:{code}}
  // envelope throws ApiError here exactly as on the parseError path (one class
  // per dialect; the runner's ApiError handling — validation-field focus — is
  // reached for both). 200 responses pass through untouched, SSE included —
  // the caller branches on content-type text/event-stream. 503 cold_start +
  // 429 rate-limit remain the caller-layer policy's (src/lib/request-policy.ts)
  // business per §4.3 — they arrive as typed errors like any other non-OK.
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    const err = classifyErrorBody(res, text, parseRetryAfter(res))
    if (res.status >= 500) reportError(err, 'api_error', { extra: { endpoint: path } })
    throw err
  }
  return res
}

/**
 * One parsed Server-Sent-Events frame. `event` is the frame's event type —
 * 'message' when the wire carries no `event:` field (the WHATWG default);
 * `data` is the `data:` payload with multi-line data fields joined by '\n';
 * `id` is the frame's `id:` value when present (NUL-containing ids ignored).
 */
export type SSEEvent = { event: string; data: string; id?: string }

/**
 * Incremental WHATWG event-stream parser. Feed decoded text chunks; each call
 * returns the frames completed by that chunk. Lines may terminate CRLF, CR, or
 * LF; a trailing partial line stays buffered until the next chunk completes
 * it. Blank line dispatches the accumulated frame; `: …` lines are comments;
 * field/value split on the first ':' with at most one leading space stripped
 * (a line with no ':' is a bare field with value ''). `retry:` and unknown
 * fields are ignored. Dispatch emits only frames with a non-empty joined data
 * payload (spec parity — event-only/id-only frames carry nothing for
 * consumers), and `data: [DONE]` stays the OpenAI sentinel: skipped, never
 * yielded. `flush === true` at stream end processes the trailing partial line
 * and dispatches any accumulated frame.
 */
export function sseParseStream(): (chunk: string, flush?: boolean) => SSEEvent[] {
  let buf = ''
  // frame accumulator — data lines collect until the blank-line dispatch
  let data: string[] = []
  let event = ''
  let id: string | undefined

  function dispatch(): SSEEvent | null {
    const joined = data.join('\n')
    const frame: SSEEvent | null =
      joined !== '' && joined !== '[DONE]' ? { event: event || 'message', data: joined, id } : null
    data = []
    event = ''
    id = undefined
    return frame
  }

  function processLine(line: string, out: SSEEvent[]): void {
    if (line === '') {
      const frame = dispatch()
      if (frame) out.push(frame)
      return
    }
    if (line.startsWith(':')) return // comment line — ignored
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'data') data.push(value)
    else if (field === 'event') event = value
    else if (field === 'id') {
      if (!value.includes('\0')) id = value
    }
    // retry / unknown fields carry nothing for consumers — ignored per spec
  }

  return (chunk: string, flush = false) => {
    buf += chunk
    // a trailing CR may be the first half of a CRLF split across chunks — hold
    // it in buf until the next chunk disambiguates (flush forces it through
    // as a lone CR line ending)
    const holdCR = !flush && buf.endsWith('\r')
    const lines = buf.slice(0, holdCR ? -1 : buf.length).split(/\r\n|\r|\n/)
    buf = (lines.pop() ?? '') + (holdCR ? '\r' : '')
    const out: SSEEvent[] = []
    for (const line of lines) processLine(line, out)
    if (flush) {
      if (buf !== '') {
        processLine(buf, out)
        buf = ''
      }
      const frame = dispatch()
      if (frame) out.push(frame)
    }
    return out
  }
}

// readSSE yields one SSEEvent per dispatched frame — `event` is the wire event
// type ('message' for anonymous data:-only frames), `data` the joined payload.
// 503 cold_start / 429 retryAfter are transport-level: gatewayFetch throws
// typed ApiError / GatewayApiError for every non-OK response, and the
// caller-layer policy (src/lib/request-policy.ts) holds/retries and arms
// countdowns before this generator runs. Mid-stream error events
// (data: {"error":…}) surface as frame.data for the caller to parse.
export async function* readSSE(res: Response): AsyncGenerator<SSEEvent, void, unknown> {
  if (!res.body) return
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  const parse = sseParseStream()
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      const text = decoder.decode(value, { stream: true })
      for (const frame of parse(text)) {
        yield frame
      }
    }
    // stream ended — flush dispatches the trailing partial line + accumulated frame
    for (const frame of parse('', true)) {
      yield frame
    }
  } finally {
    reader.releaseLock()
  }
}
